import { useRouteLocale } from '@/hooks/useRouteLocale';
import { formatClockTime, weekdayName } from '@/i18n/format';
import { useTranslate } from '@/i18n/LocaleLanguage';
import { weeklyHours } from '@/seo/openingHours';
import type { OpeningHours } from '@/types';

export interface OpeningHoursCardProps {
  /** The shop's or store's rows (backend B3.4); `[]` when nothing was entered. */
  openingHours?: readonly OpeningHours[] | null;
  className?: string;
}

/**
 * A shop's or store's opening hours, Monday to Sunday: each day's ranges in
 * the route locale's clock style ("10:00 am – 10:00 pm"), "Closed" for a day
 * in no row, "Open 24 hours" for 00:00 – 24:00, split shifts side by side. A
 * range past midnight is listed under the day it opens. Nothing at all when
 * no hours were entered (never seven "Closed" rows). The JSON-LD's
 * `openingHoursSpecification` is built from the same rows (@/seo/openingHours).
 *
 * The heading is an h2 so the page's outline never skips a level whatever the
 * main column holds; it sits in the aside, styled like the Contact card.
 */
export function OpeningHoursCard({ openingHours, className }: OpeningHoursCardProps) {
  const t = useTranslate();
  const locale = useRouteLocale();
  const week = weeklyHours(openingHours);
  if (!week) return null;
  const title = t('common:hours.title');
  return (
    <section aria-label={title} className={`space-y-3 rounded-2xl border border-line bg-card p-4 ${className ?? ''}`}>
      <h2 className="text-sm font-bold text-ink">{title}</h2>
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
        {week.map(({ day, ranges, allDay }) => (
          <div key={day} className="contents">
            <dt className="font-semibold text-ink">{weekdayName(day, locale)}</dt>
            <dd className="text-ink-secondary">
              {ranges.length === 0
                ? t('common:hours.closed')
                : allDay
                  ? t('common:hours.allDay')
                  : ranges
                      .map((range) =>
                        t('common:hours.range', {
                          opens: formatClockTime(range.opens, locale),
                          closes: formatClockTime(range.closes, locale),
                        }),
                      )
                      .join(', ')}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

export default OpeningHoursCard;
